import SelectMoreItemsHeader from 'app/components/selectMoreItems/SelectMoreItemsHeader';
import SearchTextInput from 'app/components/SearchTextInput';
import React, {
  FC,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Alert } from '@kontentino/ui';
import { SocialLoginBusiness } from 'types/SocialLogin';
import SelectSocialProfileOption, {
  SocialProfile,
} from './SelectSocialProfileOption';

// What a portfolio's group header marks: the person is not an admin there,
// or its pages were read only in part. The name labels a portfolio that came
// with no pages (the two-column picker still lists a partial one).
export type PortfolioMarkers = Pick<
  SocialLoginBusiness,
  'id' | 'is_admin' | 'partial'
> &
  Partial<Pick<SocialLoginBusiness, 'name'>>;

type Props = {
  profiles: SocialProfile[];
  portfolios?: PortfolioMarkers[];
  onSelect: (id: number | string) => void;
  selectedIds: (number | string)[];
  additionalHeader?: string;
  headerClassName?: string;
  listClassName?: string;
  isPartial?: boolean;
  onSelectAll?: (filteredIds: (number | string)[]) => void;
  onSelectNone?: (e: React.MouseEvent) => void;
  // 'modal' (default) stays inside the shared modal's height cap, in any
  // host; 'page' leaves the height to the host (onboarding's step).
  layout?: 'modal' | 'page';
};

// Business groups first, then "Other", then the pages outside every portfolio.
enum GroupRank {
  Business,
  Other,
  Outside,
}

// Cannot collide with a Meta business id.
const OUTSIDE_GROUP_KEY = 'outside-portfolios';

const isOutsidePortfolios = (profile: SocialProfile) =>
  profile.relation === 'personal';

type GroupKey = {
  key: string;
  label: string;
  rank: GroupRank;
  isNotAdmin?: boolean;
  isPartial?: boolean;
};

type Group = Omit<GroupKey, 'key'> & { profiles: SocialProfile[] };

const matchesSearch = (value: string, search: string) =>
  value.toLowerCase().includes(search);

const SelectSocialProfiles: FC<Props> = ({ layout = 'modal', ...props }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const trimmedSearch = search.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;

  const filteredProfiles = useMemo(() => {
    if (!trimmedSearch) {
      return props.profiles;
    }

    return props.profiles.filter(
      (profile) =>
        matchesSearch(profile.name, trimmedSearch) ||
        (profile.businessName
          ? matchesSearch(profile.businessName, trimmedSearch)
          : false),
    );
  }, [props.profiles, trimmedSearch]);

  // A disabled profile can never be selected, so it is neither offered to
  // select-all nor counted in the header.
  const selectableIds = useMemo(
    () =>
      filteredProfiles
        .filter((profile) => !profile.isDisabled)
        .map((profile) => profile.id),
    [filteredProfiles],
  );

  const selectedFilteredCount = useMemo(
    () => selectableIds.filter((id) => props.selectedIds.includes(id)).length,
    [selectableIds, props.selectedIds],
  );

  const hasGroups = useMemo(
    () =>
      props.profiles.some(
        (profile) => !!profile.businessId || isOutsidePortfolios(profile),
      ),
    [props.profiles],
  );

  const portfoliosById = useMemo(
    () =>
      new Map(
        (props.portfolios ?? []).map((portfolio) => [portfolio.id, portfolio]),
      ),
    [props.portfolios],
  );

  const groupedProfiles = useMemo(() => {
    const otherLabel = t('newSocialMediaProfile.other');
    const outsideLabel = t(
      'newSocialMediaProfile.oboTokenType.outsidePortfoliosHeading',
    );
    const groups = new Map<string, Group>();

    const groupKeyOf = (profile: SocialProfile): GroupKey => {
      if (isOutsidePortfolios(profile)) {
        return {
          key: OUTSIDE_GROUP_KEY,
          label: outsideLabel,
          rank: GroupRank.Outside,
        };
      }

      if (profile.businessId) {
        const portfolio = portfoliosById.get(profile.businessId);

        return {
          key: profile.businessId,
          // A portfolio with no name is shown (and sorted) by its id.
          label: profile.businessName || profile.businessId,
          rank: GroupRank.Business,
          // `null` means its roles are unknown, which is not marked.
          isNotAdmin: portfolio?.is_admin === false,
          isPartial: !!portfolio?.partial,
        };
      }

      return { key: otherLabel, label: otherLabel, rank: GroupRank.Other };
    };

    filteredProfiles.forEach((profile) => {
      const { key, ...header } = groupKeyOf(profile);
      const group = groups.get(key);

      if (group) {
        group.profiles.push(profile);
      } else {
        groups.set(key, { ...header, profiles: [profile] });
      }
    });

    return Array.from(groups.entries()).sort(
      ([, a], [, b]) => a.rank - b.rank || a.label.localeCompare(b.label),
    );
  }, [filteredProfiles, portfoliosById, t]);

  // Every row gets the same handler, which always calls the latest
  // `onSelect`: a memoised row then re-renders only when its own profile or
  // selection changes, not for every click elsewhere in the list. The ref is
  // updated after commit, so a render React discards cannot leave it pointing
  // at a callback the committed rows never saw.
  const onSelectRef = useRef(props.onSelect);
  useLayoutEffect(() => {
    onSelectRef.current = props.onSelect;
  });
  const handleRowClick = useCallback(
    (id: SocialProfile['id']) => onSelectRef.current(id),
    [],
  );

  const renderOption = (profile: SocialProfile) => {
    const isSelected = props.selectedIds.includes(profile.id);

    return (
      <SelectSocialProfileOption
        key={profile.id}
        profile={profile}
        isSelected={isSelected}
        onClick={handleRowClick}
      />
    );
  };

  const hasNoMatches = isSearching && filteredProfiles.length === 0;

  // Only the list scrolls: the warning, the header and the search above it
  // never shrink, and the list gives way to them (`min-h-0` on it and on this
  // box). A flex-column host with a capped height passes the cap down to it,
  // so its own footer stays in view too (D10). In a modal whose content is a
  // plain block (Insights), the cap here does the same: the shared modal's
  // cap, min(800px, 90vh), less a titled header (80px), the footer with Add
  // (88px) and the content's bottom margin (24px), as measured in Chromium.
  // `dvh`, so a phone's browser bar cannot cover the end.
  return (
    <div
      data-cy="select-social-profiles"
      className={clsx('tw-flex tw-min-h-0 tw-flex-col tw-gap-6', {
        'tw-max-h-[calc(min(800px,90dvh)-192px)]': layout === 'modal',
      })}
    >
      {props.isPartial && (
        <Alert
          variant="warning"
          className="tw-mt-4 tw-shrink-0"
          data-cy="select-social-profiles_partial-warning"
        >
          {t('newSocialMediaProfile.partialPagesWarning')}
        </Alert>
      )}
      <div
        className="tw-flex tw-shrink-0 tw-flex-col tw-gap-6"
        data-cy="select-social-profiles_search-block"
      >
        <SelectMoreItemsHeader
          totalCount={selectableIds.length}
          selectedCount={selectedFilteredCount}
          onSelectAll={
            props.onSelectAll
              ? () => props.onSelectAll?.(selectableIds)
              : undefined
          }
          onSelectNone={props.onSelectNone}
          className={clsx('tw-mt-4', props.headerClassName)}
        />
        <SearchTextInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          data-cy="select-social-profiles_search-input"
          data-name="select-social-profiles_search-input"
        />
      </div>
      <div
        className={clsx(
          'tw-flex tw-min-h-0 tw-flex-col tw-overflow-y-auto',
          props.listClassName,
        )}
        data-cy="select-social-profiles_list"
      >
        {props.additionalHeader && (
          <div className="tw-mb-6 tw-rounded-md tw-bg-grayscale-5 tw-px-4 tw-py-[10px]">
            <span className="tw-text-md tw-font-medium tw-text-grayscale-180">
              {props.additionalHeader}
            </span>
          </div>
        )}
        {hasNoMatches ? (
          <div
            className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-center tw-text-md tw-text-grayscale-100"
            data-cy="select-social-profiles_no-matches"
          >
            {t('newSocialMediaProfile.noProfilesMatchSearch')}
          </div>
        ) : hasGroups ? (
          <div className="tw-flex tw-flex-col tw-gap-6">
            {groupedProfiles.map(([businessId, group]) => (
              <div
                key={businessId}
                className="tw-flex tw-flex-col tw-gap-6"
                data-cy={`select-social-profiles_business-group_${businessId}`}
              >
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <span
                    role="heading"
                    aria-level={3}
                    className="tw-text-md tw-font-medium tw-text-grayscale-180"
                    data-cy="select-social-profiles_business-header"
                  >
                    {group.label}
                  </span>
                  {group.isNotAdmin && (
                    <span
                      className="tw-text-xs tw-text-grayscale-100"
                      data-cy="select-social-profiles_business-not-admin-badge"
                    >
                      {t('newSocialMediaProfile.oboTokenType.notAdminBadge')}
                    </span>
                  )}
                  {group.isPartial && (
                    <span
                      className="tw-text-xs tw-text-warning-100"
                      data-cy="select-social-profiles_business-partial-badge"
                    >
                      {t('newSocialMediaProfile.oboTokenType.partialBadge')}
                    </span>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-gap-6">
                  {group.profiles.map(renderOption)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-6">
            {filteredProfiles.map(renderOption)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectSocialProfiles;
